// ---- API Response Wrappers ----

export interface ApiResponse<T> {
  status: string;
  message: string;
  data: T;
}

export * from "./auth";

import {
  PropertyApprovalStatus,
  PropertyAvailabilityStatus,
} from "./property-config";

export type TAppUser = {
  id: string;
  full_name: string;
  email: string;
  role: string;
  kyc_status: string;
  plan?: string;
  is_partner_realtor?: string;
  profile_picture?: string;
  username?: string;
};

export type TAppUserState = {
  token: string;
  user: TAppUser;
  loginTime?: number;
};

/*
export type TProperty = {
  id?: string;
  address?: string;
  description?: string;
  status?: string; // LEGACY/DEPRECATED — still returned; use approval_status/availability_status
  approval_status?: PropertyApprovalStatus;
  availability_status?: PropertyAvailabilityStatus;
  listing_type?: string;
  price: number;
  property_type?: string;
  category?: string;
  city?: string;
  state?: string;
  furnished?: boolean;
  bedroom_count?: number;
  bathroom_count?: number;
  number_of_plots?: number;
  agency_fees?: number;
  legal_fees?: number;
  caution_fee?: number;
  total_package: number;
  property_stage?: string;
  additional_info?: string;
  number_of_days?: number;
  image_url?: string[];
  latitude?: number;
  longitude?: number;
  promote_to_weekly_deals: boolean;
  request_inspection: boolean;
  promoteWeekly: boolean;
  requestInspection: boolean;
  amenities?: string[];
  serviced?: boolean;
  shared?: boolean;
  is_negotiable?: boolean;
  property_size_sqm?: number;
  number_of_floors?: number;
  parking_spaces?: number;
  newly_built?: boolean;
  floor_area_sqm?: number;
  floor_number?: number;
  number_of_private_offices?: number;
  meeting_rooms?: number;
  conference_rooms?: number;
  reception_area?: boolean;
  open_workspace_capacity?: number;
  parking_capacity?: number;
  dedicated_desks?: number;
  private_offices?: number;
  capacity?: number;
  internet_speed?: string;
  printing_available?: boolean;
  shop_size_sqm?: number;
  storage_room?: boolean;
  electricity?: boolean;
  water_supply?: boolean;
  display_area_sqm?: number;
  office_area?: number;
  storage_area?: number;
  ceiling_height?: number;
  glass_frontage_length?: number;
  dining_capacity?: number;
  kitchen_size?: number;
  indoor_seating?: boolean;
  outdoor_seating?: boolean;
  number_of_restrooms?: number;
  private_dining_rooms?: number;
  bar_area?: boolean;
  number_of_rooms?: number;
  room_types?: string;
  number_of_suites?: number;
  conference_rooms_count?: number;
  banquet_hall?: boolean;
  hall_capacity?: number;
  number_of_event_halls?: number;
  vip_lounge?: boolean;
  bridal_room?: boolean;
  waiting_lounge?: boolean;
  dressing_room?: boolean;
  number_of_toilets?: number;
  stage?: boolean;
  led_screen?: boolean;
  sound_system?: boolean;
  lighting?: boolean;
  wheelchair_access?: boolean;
  warehouse_size?: number;
  loading_bays?: number;
  dock_levelers?: boolean;
  truck_access?: boolean;
  container_access?: boolean;
  office_attached?: boolean;
  security?: boolean;
  power_capacity?: string;
  cold_storage?: boolean;
  fire_safety_system?: boolean;
  factory_floor_area?: number;
  warehouse_area?: number;
  number_of_production_floors?: number;
  floor_load_capacity?: number;
  dock_doors?: number;
  transformer_capacity?: string;
  generator_capacity?: string;
  borehole?: boolean;
  gas_supply?: boolean;
  waste_treatment_facility?: boolean;
  workshop_area?: number;
  yard_space?: number;
  floor_strength?: string;
  three_phase_power?: boolean;
  compressed_air_lines?: boolean;
  cold_storage_capacity?: number;
  number_of_cold_rooms?: number;
  temperature_range?: string;
  freezer_capacity?: number;
  chiller_capacity?: number;
  yard_size?: number;
  number_of_dock_doors?: number;
  truck_parking_capacity?: number;
  trailer_parking_capacity?: number;
  fleet_parking?: boolean;
  container_holding_area?: boolean;
  service_charge?: number;
  sale_document_details?: string;
  documentation?: string;
};
*/

export interface HomepagePropertiesQuery {
  page: number;
  pageSize: number;
  searchQuery?: string;
  userCurrentLocation?: string;
  location?: string;
  latitude?: string;
  longitude?: string;
  realtorId?: string;

  state?: string;
  city?: string;

  propertyType?: string;
  propertyCategory?: string;
  listingType?: string;

  bedroomCount?: string;
  bathroomCount?: string;

  minPrice?: string;
  maxPrice?: string;

  furnished?: boolean;
  shared?: boolean;
  serviced?: boolean;

  addedToSite?: boolean;
}

export interface UserPropertyMetricsResponse {
  totalCount: number;
  totalCountByListingType: Partial<Record<ListingType, number>>;
  totalCountByStatus: Partial<Record<PropertyApprovalStatus, number>>;
  totalCountByAvailabilityStatus: Partial<
    Record<PropertyAvailabilityStatus, number>
  >;
  totalCountByPropertyType: Partial<Record<PropertyType, number>>;
  totalCountByTimePeriod: TimePeriodMetrics;
}

// LEGACY / DEPRECATED — see @/types/property-config for the split
// PropertyApprovalStatus / PropertyAvailabilityStatus enums.
export enum PropertyStatus {
  AVAILABLE = "available",
  UNDER_CONTRACT = "under-contract",
  UNDER_REVIEW = "under-review",
  PENDING = "pending",
  RENTED = "rented",
  LEASED = "leased",
  SOLD = "sold",
  REJECTED = "rejected",
  APPROVED = "approved",
}

export enum PropertyCategory {
  RESIDENTIAL = "residential",
  COMMERCIAL = "commercial",
  INDUSTRIAL = "industrial",
  SPECIAL_PURPOSE = "special-purpose",
}

export enum ListingType {
  SALE = "sale",
  RENT = "rent",
  SHORTLET = "shortlet",
  LAND = "land",
  BUY = "buy",
  LEASE = "lease",
}

export enum PropertyType {
  HOUSE = "house",
  APARTMENT = "apartment",
  LAND = "land",
  COMMERCIAL = "commercial",
  FLAT_AND_APARTMENTS = "flat-and-apartments",
  SEMI_DETACHED_DUPLEX = "semi-detached-duplex",
  FULLY_DETACHED_DUPLEX = "fully-detached-duplex",
  TERRACES = "terraces",
  CO_WORKING_SPACE = "co-working-space",
  SHOPS = "shops",
  COMMERCIAL_PROPERTIES = "commercial-properties",
  DETACHED_BUNGALOWS = "detached-bungalows",
  MINI_FLATS = "mini-flats",
  SELF_CONTAIN = "self-contain",
  SHOP_IN_A_MALL = "shop-in-a-mall",
  OFFICE_SPACES = "office-spaces",
  WAREHOUSES = "warehouses",
  PENTHOUSE = "penthouse",
  MAISONETTE = "maisonette",
  BLOCK_OF_FLATS = "block-of-flats",
  PLAZA = "plaza",
  RETAIL_SPACE = "retail-space",
  SHOWROOM = "showroom",
  RESTAURANT_CAFE = "restaurant-cafe",
  HOTEL_GUEST_HOUSE = "hotel-guest-house",
  EVENT_CENTRE = "event-centre",
  FACTORY = "factory",
  WORKSHOP = "workshop",
  COLD_ROOM = "cold-room",
  LOGISTICS_HUB = "logistics-hub",
  SCHOOL = "school",
  HOSPITAL = "hospital",
  CHURCH = "church",
  MOSQUE = "mosque",
  FILLING_STATION = "filling-station",
  PETROL_GAS_PLANT = "petrol-gas-plant",
  FARM = "farm",
  GOVERNMENT_PROPERTY = "government-property",
}

export interface TimePeriodMetrics {
  thisWeek: number;
  lastWeek: number;
  lastTwoWeeks: number;
  lastThreeWeeks: number;
  thisMonth: number;
  lastMonth: number;
  lastTwoMonths: number;
  lastSixMonths: number;
  thisYear: number;
  lastYear: number;
}

export interface GetPropertiesByRealtorParams {
  realtorId?: string;
  pageSize?: number;
  pageNumber?: number;
  cursor?: number;
  beforeCursor?: number;
  location?: string;
  propertyType?: PropertyType;
  status?: PropertyStatus;
}
